import { Card } from '@/types/Card';
import { CARD_KEYS, BANK_KEYS, BANK_NAMES } from '../../constants/keys';
import { FeatureType } from '@/types/Feature';
import { CardLinkMapping, BankLinkMapping } from '../cardLinks';

/**
 * Map of bank keys to their source URLs
 * This allows us to easily update bank information when they change their websites
 * The key is the bank key from BANK_KEYS
 */
export const bankLinkMap: Record<string, BankLinkMapping> = {
  [BANK_KEYS.AMERICANEXPRESS]: {
    sourceUrl:
      'https://www.americanexpress.com/zh-tw/credit-cards/all-cards/?inav=ta_menu_card_personal',
    lastUpdated: '2025-05-04',
  },
};

export const cardLinkMap: Record<string, CardLinkMapping> = {
  [CARD_KEYS.AMERICANEXPRESS_PLAT]: {
    sourceUrl:
      'https://www.americanexpress.com/zh-tw/charge-cards/plat/?linknav=tw-amex-cardshop-details-browse-Plat',
    applyUrl:
      'https://www.americanexpress.com/zh-tw/charge-cards/plat/?linknav=tw-amex-cardshop-details-browse-Plat',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.AMERICANEXPRESS_GOLD]: {
    sourceUrl: 'https://www.americanexpress.com/zh-tw/credit-cards/grcc-gold/',
    applyUrl: 'https://www.americanexpress.com/zh-tw/credit-cards/grcc-gold/',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.AMERICANEXPRESS_CREDIT_PLAT]: {
    sourceUrl:
      'https://www.americanexpress.com/zh-tw/charge-cards/plat/?linknav=tw-amex-cardshop-details-browse-Plat',
    applyUrl:
      'https://www.americanexpress.com/zh-tw/charge-cards/plat/?linknav=tw-amex-cardshop-details-browse-Plat',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.AMERICANEXPRESS_EVERGREEN]: {
    sourceUrl:
      'https://www.americanexpress.com/zh-tw/charge-cards/green/?linknav=tw-amex-cardshop-details-browse-Green',
    applyUrl:
      'https://www.americanexpress.com/zh-tw/charge-cards/green/?linknav=tw-amex-cardshop-details-browse-Green',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.AMERICANEXPRESS_JINHWA]: {
    sourceUrl:
      'https://www.americanexpress.com/zh-tw/credit-cards/regent-plat/?linknav=tw-amex-cardshop-details-browse-RegentPlat',
    applyUrl:
      'https://www.americanexpress.com/zh-tw/credit-cards/regent-plat/?linknav=tw-amex-cardshop-details-browse-RegentPlat',
    lastUpdated: '2025-06-21',
  },
};

export const americanExpressCards: { [key: string]: Card } = {
  [CARD_KEYS.AMERICANEXPRESS_PLAT]: {
    name: '美國運通白金卡',
    summary: '頂級旅遊禮遇、機場貴賓室無限次使用',
    issuing_bank: BANK_NAMES[BANK_KEYS.AMERICANEXPRESS],
    payment_network: 'American Express',
    picture: 'logo_americanexpress-plat.png',
    key: 'americanexpress-plat',
    link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_PLAT].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_PLAT].applyUrl,
    features: [
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '無限次使用',
        simplified_condition: 'Priority Pass會員資格',
        condition: '持卡人可獲得Priority Pass會員資格，享全球1,300多間機場貴賓室無限次使用',
        channel: '機場貴賓室',
      },
      {
        type: FeatureType.TRAVEL,
        feedback: 'NT$10,000旅遊金',
        max_feedback: '每年一次',
        simplified_condition: '指定旅遊平台預訂',
        condition: '透過美國運通旅遊服務預訂指定旅遊產品，可獲得NT$10,000旅遊金，每年一次',
        channel: '旅遊',
      },
      {
        type: FeatureType.HOTEL,
        feedback: '免費住宿',
        max_feedback: '每年一晚',
        simplified_condition: '指定豪華酒店',
        condition:
          '在Fine Hotels + Resorts®計劃中的指定豪華酒店預訂連續兩晚或以上，可獲贈一晚免費住宿',
        channel: '酒店',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$30=1哩',
        max_feedback: '無上限',
        simplified_condition: '一般消費',
        condition: '一般消費每NT$30可獲得1哩飛行里數，可轉換至多家航空公司里程計劃',
        channel: '里程',
      },
    ],
    domestic_feedback: 'NT$30=1哩',
    international_feedback: 'NT$30=1哩',
    specific_channel_feedback: '機場貴賓室無限次',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.AMERICANEXPRESS_GOLD]: {
    name: '美國運通金卡',
    summary: '尊榮旅遊禮遇、餐飲優惠',
    issuing_bank: BANK_NAMES[BANK_KEYS.AMERICANEXPRESS],
    payment_network: 'American Express',
    picture: 'logo_americanexpress-gold.png',
    key: 'americanexpress-gold',
    link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_GOLD].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_GOLD].applyUrl,
    features: [
      {
        type: FeatureType.DISCOUNT,
        feedback: '精選餐廳優惠',
        max_feedback: '買一送一',
        simplified_condition: '指定餐廳',
        condition: '在指定精選餐廳消費可享買一送一或其他專屬優惠',
        channel: '餐飲',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場接送',
        max_feedback: '每年4次',
        simplified_condition: '單筆消費滿NT$10,000',
        condition: '單筆消費滿NT$10,000，可獲得機場接或送服務，每年最多4次',
        channel: '機場接送',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$30=1哩',
        max_feedback: '無上限',
        simplified_condition: '一般消費',
        condition: '一般消費每NT$30可獲得1哩飛行里數，可轉換至多家航空公司里程計劃',
        channel: '里程',
      },
    ],
    domestic_feedback: 'NT$30=1哩',
    international_feedback: 'NT$30=1哩',
    specific_channel_feedback: '餐飲優惠',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.AMERICANEXPRESS_CREDIT_PLAT]: {
    name: '美國運通信用白金卡',
    summary: '頂級信用卡禮遇、旅遊保障',
    issuing_bank: BANK_NAMES[BANK_KEYS.AMERICANEXPRESS],
    payment_network: 'American Express',
    picture: 'logo_americanexpress-credit-plat.png',
    key: 'americanexpress-credit-plat',
    link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_CREDIT_PLAT].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_CREDIT_PLAT].applyUrl,
    features: [
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊保險',
        max_feedback: '最高NT$5,000萬',
        simplified_condition: '刷卡購買機票',
        condition: '刷卡購買機票可獲得最高NT$5,000萬旅遊保險保障',
        channel: '旅遊',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1%現金回饋，無金額上限',
        channel: '一般消費',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年2次',
        simplified_condition: '持卡人',
        condition: '持卡人每年可享2次機場貴賓室使用權',
        channel: '機場貴賓室',
      },
    ],
    domestic_feedback: '1%回饋',
    international_feedback: '1%回饋',
    specific_channel_feedback: '旅遊保障',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.AMERICANEXPRESS_EVERGREEN]: {
    name: '美國運通綠卡',
    summary: '經典卡片、基本禮遇',
    issuing_bank: BANK_NAMES[BANK_KEYS.AMERICANEXPRESS],
    payment_network: 'American Express',
    picture: 'logo_americanexpress-evergreen.png',
    key: 'americanexpress-evergreen',
    link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_EVERGREEN].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_EVERGREEN].applyUrl,
    features: [
      {
        type: FeatureType.BENEFIT,
        feedback: '基本會員服務',
        max_feedback: '全球支援',
        simplified_condition: '持卡人',
        condition: '持卡人可享美國運通基本會員服務與全球支援',
        channel: '會員服務',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$30=1哩',
        max_feedback: '無上限',
        simplified_condition: '一般消費',
        condition: '一般消費每NT$30可獲得1哩飛行里數',
        channel: '里程',
      },
    ],
    domestic_feedback: 'NT$30=1哩',
    international_feedback: 'NT$30=1哩',
    specific_channel_feedback: '基本禮遇',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.AMERICANEXPRESS_JINHWA]: {
    name: '美國運通晶華珍饌信用卡',
    summary: '頂級餐飲禮遇、晶華集團優惠',
    issuing_bank: BANK_NAMES[BANK_KEYS.AMERICANEXPRESS],
    payment_network: 'American Express',
    picture: 'logo_americanexpress-jinhwa.png',
    key: 'americanexpress-jinhwa',
    link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_JINHWA].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_JINHWA].applyUrl,
    features: [
      {
        type: FeatureType.DISCOUNT,
        feedback: '晶華集團85折',
        max_feedback: '無上限',
        simplified_condition: '晶華集團消費',
        condition: '在晶華集團旗下餐廳消費享85折優惠',
        channel: '餐飲',
      },
      {
        type: FeatureType.HOTEL,
        feedback: '住宿升等',
        max_feedback: '視房況而定',
        simplified_condition: '晶華酒店',
        condition: '在晶華酒店住宿可享房間升等優惠，視房況而定',
        channel: '酒店',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2%回饋',
        max_feedback: '晶華集團消費',
        simplified_condition: '晶華集團',
        condition: '在晶華集團消費可享2%現金回饋',
        channel: '晶華集團',
      },
    ],
    domestic_feedback: '1%回饋',
    international_feedback: '1%回饋',
    specific_channel_feedback: '晶華集團2%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
};
